import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { Loader2, Repeat, Users } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { createCalendarEventFull } from "@/lib/calendarApi";
import type { RecurrenceChoice, WeekDay } from "@/lib/calendarTypes";
import { RECURRENCE_OPTIONS, SHOW_AS_OPTIONS, WEEKDAY_OPTIONS, type RecurrenceUiPattern } from "./types";

/**
 * Extracted verbatim from client/src/pages/Calendar.tsx — no behavior change
 * on /calendar. `dialogTitle` lets callers (the pop-out inbox's "New event"
 * flow) relabel the dialog without duplicating the component; the page
 * itself keeps calling it "Block Time" (Wade's rule — the button label on
 * /calendar stays "Block Time", only the pop-out says "New event").
 */
export function BlockTimeModal({
  defaultDate,
  onClose,
  onSaved,
  dialogTitle = "Add Calendar Event",
}: {
  defaultDate?: string;
  onClose: () => void;
  onSaved: () => void;
  dialogTitle?: string;
}) {
  // NOTE: this always creates the event on the AUTHENTICATED user's own
  // calendar — there is no targetEmail here by design (Session-163 rule,
  // closed server-side in Phase B: POST /api/calendar/events no longer
  // accepts a client-supplied target).
  const { toast } = useToast();
  const today = new Date().toISOString().split("T")[0];
  const [subject, setSubject] = useState("");
  const [date, setDate] = useState(defaultDate ?? today);
  const [startTime, setStartTime] = useState("09:00");
  const [endTime, setEndTime] = useState("10:00");
  const [isAllDay, setIsAllDay] = useState(false);
  const [showAs, setShowAs] = useState<"free" | "busy" | "tentative" | "oof" | "workingElsewhere">("busy");
  const [notes, setNotes] = useState("");

  // Recurrence
  const [recurPattern, setRecurPattern] = useState<RecurrenceUiPattern>("none");
  const [recurDays, setRecurDays] = useState<Set<WeekDay>>(new Set());
  const [recurDayOfMonth, setRecurDayOfMonth] = useState<number>(1);
  const [recurEndType, setRecurEndType] = useState<"date" | "count">("count");
  const [recurEndDate, setRecurEndDate] = useState("");
  const [recurCount, setRecurCount] = useState<number>(5);

  // Attendees — plain multi-attendee invite (no Zoom), reusing a simple
  // comma/email-chip pattern (lighter than ScheduleMeetingModal's, since
  // Block Time has no client-contact list to pick from).
  const [attendeeInput, setAttendeeInput] = useState("");
  const [attendees, setAttendees] = useState<string[]>([]);

  function addAttendee() {
    const email = attendeeInput.trim();
    if (email && /\S+@\S+\.\S+/.test(email) && !attendees.includes(email)) {
      setAttendees((prev) => [...prev, email]);
    }
    setAttendeeInput("");
  }

  function buildRecurrence(): RecurrenceChoice | undefined {
    if (recurPattern === "none") return undefined;
    const base = {
      endType: recurEndType,
      endDate: recurEndType === "date" ? recurEndDate : undefined,
      count: recurEndType === "count" ? recurCount : undefined,
    };
    if (recurPattern === "weekly") {
      return { pattern: "weekly", daysOfWeek: Array.from(recurDays), ...base };
    }
    if (recurPattern === "monthly") {
      return { pattern: "monthly", dayOfMonth: recurDayOfMonth, ...base };
    }
    return { pattern: "daily", ...base };
  }

  const mutation = useMutation({
    mutationFn: () =>
      createCalendarEventFull({
        subject: subject.trim(),
        date,
        startTime: isAllDay ? undefined : startTime,
        endTime: isAllDay ? undefined : endTime,
        isAllDay,
        showAs,
        notes: notes.trim() || undefined,
        attendees: attendees.length ? attendees.map((email) => ({ email })) : undefined,
        recurrence: buildRecurrence() ?? undefined,
      }),
    onSuccess: () => {
      toast({ title: "Event added to calendar" });
      onSaved();
      onClose();
    },
    onError: (err: Error) => {
      toast({
        title: "Failed to add event",
        description: err.message.includes("Calendars.ReadWrite")
          ? "Calendars.ReadWrite permission required — ask your admin to grant it in the Azure portal."
          : err.message,
        variant: "destructive",
      });
    },
  });

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{dialogTitle}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-1">
          <div className="space-y-1.5">
            <Label htmlFor="block-subject">Title</Label>
            <Input
              id="block-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="e.g. Lunch, Day off, Focus time…"
              autoFocus
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              id="block-allday"
              type="checkbox"
              checked={isAllDay}
              onChange={(e) => setIsAllDay(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300"
            />
            <Label htmlFor="block-allday" className="cursor-pointer">All day</Label>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="block-date">Date</Label>
            <Input
              id="block-date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </div>

          {!isAllDay && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="block-start">Start (ET)</Label>
                <Input
                  id="block-start"
                  type="time"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="block-end">End (ET)</Label>
                <Input
                  id="block-end"
                  type="time"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                />
              </div>
            </div>
          )}

          <div className="space-y-1.5">
            <Label>Show as</Label>
            <Select value={showAs} onValueChange={(v) => setShowAs(v as typeof showAs)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SHOW_AS_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="block-notes">Notes <span className="text-muted-foreground font-normal text-xs">— optional</span></Label>
            <Input
              id="block-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional description…"
            />
          </div>

          <div className="space-y-1.5">
            <Label className="flex items-center gap-1"><Repeat className="w-3.5 h-3.5" /> Repeat</Label>
            <Select value={recurPattern} onValueChange={(v) => setRecurPattern(v as RecurrenceUiPattern)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {RECURRENCE_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {recurPattern === "weekly" && (
            <div className="flex flex-wrap gap-1.5">
              {WEEKDAY_OPTIONS.map((d) => {
                const active = recurDays.has(d.value);
                return (
                  <button
                    key={d.value}
                    type="button"
                    onClick={() =>
                      setRecurDays((prev) => {
                        const next = new Set(prev);
                        if (next.has(d.value)) next.delete(d.value);
                        else next.add(d.value);
                        return next;
                      })
                    }
                    className={cn(
                      "px-2 py-1 rounded text-xs border transition-colors",
                      active ? "bg-primary text-primary-foreground border-primary" : "bg-muted/40 border-border text-muted-foreground"
                    )}
                  >
                    {d.label}
                  </button>
                );
              })}
            </div>
          )}

          {recurPattern === "monthly" && (
            <div className="space-y-1.5">
              <Label htmlFor="block-day-of-month">Day of month</Label>
              <Input
                id="block-day-of-month"
                type="number"
                min={1}
                max={31}
                value={recurDayOfMonth}
                onChange={(e) => setRecurDayOfMonth(Number(e.target.value) || 1)}
              />
            </div>
          )}

          {recurPattern !== "none" && (
            <div className="space-y-1.5">
              <Label>Ends</Label>
              <div className="flex items-center gap-2">
                <Select value={recurEndType} onValueChange={(v) => setRecurEndType(v as "date" | "count")}>
                  <SelectTrigger className="w-28">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="count">After</SelectItem>
                    <SelectItem value="date">On date</SelectItem>
                  </SelectContent>
                </Select>
                {recurEndType === "count" ? (
                  <Input
                    type="number"
                    min={1}
                    value={recurCount}
                    onChange={(e) => setRecurCount(Number(e.target.value) || 1)}
                    className="w-24"
                  />
                ) : (
                  <Input
                    type="date"
                    value={recurEndDate}
                    onChange={(e) => setRecurEndDate(e.target.value)}
                  />
                )}
                {recurEndType === "count" && <span className="text-xs text-muted-foreground">occurrences</span>}
              </div>
            </div>
          )}

          <div className="space-y-1.5">
            <Label className="flex items-center gap-1"><Users className="w-3.5 h-3.5" /> Invite attendees <span className="text-muted-foreground font-normal text-xs">— optional, no Zoom</span></Label>
            <div className="flex gap-2">
              <Input
                value={attendeeInput}
                onChange={(e) => setAttendeeInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addAttendee();
                  }
                }}
                placeholder="name@example.com"
              />
              <Button type="button" variant="outline" size="sm" onClick={addAttendee}>Add</Button>
            </div>
            {attendees.length > 0 && (
              <div className="flex flex-wrap gap-1.5 pt-1">
                {attendees.map((email) => (
                  <Badge key={email} variant="secondary" className="gap-1">
                    {email}
                    <button
                      type="button"
                      onClick={() => setAttendees((prev) => prev.filter((e) => e !== email))}
                      className="ml-0.5 hover:text-destructive"
                    >
                      ×
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            disabled={!subject.trim() || !date || mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            {mutation.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Add Event
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
